import { drawChart, syncOverlayIndicatorPriceRange } from "./chart/draw";
import { markStaticDirty } from "./chart/dirty";
import { createChartState, createMarketSelection, GUTTERS } from "./chart/state";
import { TIMEFRAME } from "./chart/timeFrame";
import type {
  ChartDisplaySettings,
  DrawingState,
  IndicatorState,
  MarketSelection,
  State,
  Timeframe,
  TradeState,
} from "./chart/types";
import {
  createFloatingLatestButton,
  createTradingWorkspaceControls,
  createTradePanel,
} from "./chart/chartControls";
import { setupChartEvents } from "./chart/events";
import {
  bindChartPersistence,
  clearChartPersistence,
  restoreChartPersistence,
  type PersistenceScope,
} from "./chart/persistence";
import { liveDataManager, type LiveDataFeed } from "./chart/liveData";
import {
  getEarliestDataTime,
  getLatestDataTime,
  normalizeChartDataPoints,
  type ChartDataInputPoint,
  type ChartDataPoint,
} from "./chart/data";
import type { ChartDataStore } from "./chart/dataStore";
import { generateMasterData } from "./chart/hardcodedData";
import { fitPriceRangeInstant } from "./chart/price";
import { goToLatest } from "./chart/time";
import { clearAllCache, invalidateTimeframeCache, precomputeTimeframeCache, ticksToOHLC } from "./chart/ohlc";
import { hasActiveTrades } from "./chart/trades";
import { applyThemeToInterface, type ChartThemeName } from "./chart/theme";
import type { ChartModel } from "./chart/model";

const SIMULATED_LOAD_MS = 220;
const CANDLE_BASED_CHART_TYPES: Array<State["chartType"]> = ["candle", "hollow", "ohlc"];

function normalizeChartTypeForTimeframe(state: State): void {
  if (state.timeframe !== "1t") return;
  if (!CANDLE_BASED_CHART_TYPES.includes(state.chartType)) return;
  state.chartType = "line";
}

type ChartTarget = string | HTMLElement;
export type ChartMainDataPoint = ChartDataPoint;
export type ChartDataValue = ChartDataInputPoint | ChartDataPoint;

type ChartBaseOptions = {
  target: ChartTarget;
  controlsContainer?: HTMLElement;
  autoStartLiveData?: boolean;
  state?: State;
  model?: ChartModel;
  marketSelection?: MarketSelection;
  indicatorState?: IndicatorState;
  drawingState?: DrawingState;
  tradeState?: TradeState;
  dataStore?: ChartDataStore;
  liveData?: LiveDataFeed;
  theme?: ChartThemeName;
  data?: ChartDataValue[];
  demoData?: boolean;
  demoDataCount?: number;
  demoStartPrice?: number;
  demoPipSize?: number;
  liveDataUrl?: string;
  symbol?: string;
  authToken?: string;
  authRequest?: Record<string, unknown>;
  subscribeRequest?: Record<string, unknown>;
  historyRequest?: Record<string, unknown>;
  historyCount?: number;
  historyStart?: string | number;
  historyEnd?: string | number;
  displaySettings?: ChartDisplaySettings;
  displaySettingsPatch?: Partial<ChartDisplaySettings>;
  tightPriceScale?: boolean;
  userId?: string;
  onMarketChange?: (symbol: string) => void;
  onLeftEdgeReached?: () => void;
  onTimeframeChange?: (timeframe: Timeframe) => void;
};

export type ChartOptions = ChartBaseOptions;

export type ChartMainOptions = ChartBaseOptions & {
  showControls?: boolean;
  showTradePanel?: boolean;
  showMarketSelector?: boolean;
  showTimeframeSelector?: boolean;
  showIndicatorsButton?: boolean;
  showDrawingsButton?: boolean;
  showSettingsButton?: boolean;
  showChartTypeButton?: boolean;
  iconSize?: number;
  isMarketLive?: boolean;
  marketStatus?: "open" | "closed" | "unknown";
};

export type ChartMainInstance = {
  container: HTMLElement;
  canvas: HTMLCanvasElement;
  state: State;
  requestRender: () => void;
  setTimeframe: (tf: Timeframe) => void;
  setTheme: (theme: ChartThemeName) => void;
  setDisplaySettings: (settings: Partial<ChartDisplaySettings>) => void;
  setMarketSymbol: (symbol: string) => void;
  resize: () => void;
  destroy: () => void;
  clearUserSetup: () => void;
  onMarketChange: (callback: (symbol: string) => void) => () => void;
  resetForNewMarket: (symbol: string) => void;
  getCurrentSymbol: () => string;
  setHistoryLoading: (loading: boolean) => void;
};

function resolveTarget(target: ChartTarget): HTMLElement {
  if (typeof target !== "string") return target;
  const byId = document.getElementById(target) ?? document.querySelector<HTMLElement>(target);
  if (!byId) throw new Error(`Target container not found: ${target}`);
  return byId;
}

function bindStateSlices(
  state: State,
  slices: {
    indicatorState?: IndicatorState;
    drawingState?: DrawingState;
    tradeState?: TradeState;
  },
): void {
  if (slices.indicatorState) {
    const indicatorState = slices.indicatorState;
    Object.defineProperties(state, {
      indicatorType: {
        get: () => indicatorState.indicatorType,
        set: (value: IndicatorState["indicatorType"]) => {
          indicatorState.indicatorType = value;
        },
        configurable: true,
        enumerable: true,
      },
      indicatorPeriod: {
        get: () => indicatorState.indicatorPeriod,
        set: (value: number) => {
          indicatorState.indicatorPeriod = value;
        },
        configurable: true,
        enumerable: true,
      },
      indicatorColor: {
        get: () => indicatorState.indicatorColor,
        set: (value: string) => {
          indicatorState.indicatorColor = value;
        },
        configurable: true,
        enumerable: true,
      },
      indicatorLineWidth: {
        get: () => indicatorState.indicatorLineWidth,
        set: (value: number) => {
          indicatorState.indicatorLineWidth = value;
        },
        configurable: true,
        enumerable: true,
      },
      indicators: {
        get: () => indicatorState.indicators,
        set: (value) => {
          indicatorState.indicators = value;
        },
        configurable: true,
        enumerable: true,
      },
    });
  }

  if (slices.drawingState) {
    const drawingState = slices.drawingState;
    Object.defineProperties(state, {
      activeDrawingTool: {
        get: () => drawingState.activeDrawingTool,
        set: (value: DrawingState["activeDrawingTool"]) => {
          drawingState.activeDrawingTool = value;
        },
        configurable: true,
        enumerable: true,
      },
      drawings: {
        get: () => drawingState.drawings,
        set: (value) => {
          drawingState.drawings = value;
        },
        configurable: true,
        enumerable: true,
      },
      currentDrawing: {
        get: () => drawingState.currentDrawing,
        set: (value) => {
          drawingState.currentDrawing = value;
        },
        configurable: true,
        enumerable: true,
      },
      selectedDrawingId: {
        get: () => drawingState.selectedDrawingId,
        set: (value) => {
          drawingState.selectedDrawingId = value;
        },
        configurable: true,
        enumerable: true,
      },
      hoveredDrawingId: {
        get: () => drawingState.hoveredDrawingId,
        set: (value) => {
          drawingState.hoveredDrawingId = value;
        },
        configurable: true,
        enumerable: true,
      },
      draggingDrawing: {
        get: () => drawingState.draggingDrawing,
        set: (value) => {
          drawingState.draggingDrawing = value;
        },
        configurable: true,
        enumerable: true,
      },
    });
  }

  if (slices.tradeState) {
    const tradeState = slices.tradeState;
    Object.defineProperties(state, {
      trades: {
        get: () => tradeState.trades,
        set: (value) => {
          tradeState.trades = value;
        },
        configurable: true,
        enumerable: true,
      },
      tradeStyles: {
        get: () => tradeState.tradeStyles,
        set: (value) => {
          tradeState.tradeStyles = value;
        },
        configurable: true,
        enumerable: true,
      },
    });
  }
}

function createChartBase(options: ChartBaseOptions & {
  showControls: boolean;
  showTradePanel?: boolean
  showMarketSelector?: boolean;
  showTimeframeSelector?: boolean;
  showIndicatorsButton?: boolean;
  showDrawingsButton?: boolean;
  showSettingsButton?: boolean;
  showChartTypeButton?: boolean;
  iconSize?: number;
  isMarketLive?: boolean;
  marketStatus?: "open" | "closed" | "unknown";
}): ChartMainInstance {
  const container = resolveTarget(options.target);
  container.classList.add("chart-embedded-chart");
  const showControls = options.showControls;
  const showTradePanel = options.showTradePanel ?? false;

  // Individual control flags - default to showControls value if not specified
  const showMarketSelector = options.showMarketSelector ?? showControls;
  const showTimeframeSelector = options.showTimeframeSelector ?? showControls;
  const showIndicatorsButton = options.showIndicatorsButton ?? showControls;
  const showDrawingsButton = options.showDrawingsButton ?? showControls;
  // Temporarily mute the settings trigger on the chart side while keeping the
  // underlying settings panel code intact for future re-enable.
  const showSettingsButton = false && (options.showSettingsButton ?? showControls);
  const showChartTypeButton = options.showChartTypeButton ?? showControls;
  const model = options.model;
  const marketSelection = options.marketSelection ?? model?.marketSelection ?? createMarketSelection(options.symbol ?? "VIX_10");
  const symbol = marketSelection.symbol ?? options.symbol ?? "VIX_10";
  const persistenceScope: PersistenceScope = {
    userId: options.userId,
    authToken: options.authToken,
    symbol,
  };
  const liveData = options.liveData ?? liveDataManager;
  const hasLiveOptions =
    Boolean(options.liveData) ||
    Boolean(options.liveDataUrl) ||
    Boolean(options.symbol) ||
    Boolean(options.authToken) ||
    Boolean(options.authRequest) ||
    Boolean(options.subscribeRequest) ||
    Boolean(options.historyRequest) ||
    Boolean(options.historyCount) ||
    Boolean(options.historyStart) ||
    Boolean(options.historyEnd);
  const staticData = options.data ?? (
    options.demoData
      ? generateMasterData(
        options.demoDataCount ?? 200000,
        1,
        options.demoStartPrice ?? 500,
        options.demoPipSize ?? 3,
        symbol,
      )
      : undefined
  );
  const hasStaticData = Array.isArray(staticData) || Boolean(options.dataStore);
  const autoStartLiveData = options.autoStartLiveData ?? hasLiveOptions;
  const shouldBindLiveData = Boolean(options.liveData) || autoStartLiveData;
  const getData = () => {
    if (options.dataStore) return options.dataStore.getData();
    return hasStaticData ? state.chartData : liveData.getData();
  };

  if (autoStartLiveData && hasLiveOptions) {
    liveData.configure({
      ...(options.liveDataUrl ? { url: options.liveDataUrl } : {}),
      ...(options.symbol ? { symbol } : {}),
      ...(options.authToken ? { authToken: options.authToken } : {}),
      ...(options.authRequest ? { authRequest: options.authRequest } : {}),
      ...(options.subscribeRequest ? { subscribeRequest: options.subscribeRequest } : {}),
      ...(options.historyRequest ? { historyRequest: options.historyRequest } : {}),
      ...(options.historyCount ? { historyCount: options.historyCount } : {}),
      ...(options.historyStart ? { historyStart: options.historyStart } : {}),
      ...(options.historyEnd ? { historyEnd: options.historyEnd } : {}),
    });
  }

  container.innerHTML = "";
  if (getComputedStyle(container).position === "static") container.style.position = "relative";

  const shell = document.createElement("div");
  shell.className = showControls ? "chart-main-shell" : "chart-main-shell chart-main-shell--solo";
  container.appendChild(shell);

  const chartArea = document.createElement("div");
  chartArea.className = "chart-main-chart-area";
  shell.appendChild(chartArea);

  const chartStage = document.createElement("div");
  chartStage.className = "chart-main-chart-stage";
  chartArea.appendChild(chartStage);

  const canvas = document.createElement("canvas");
  const ctxMaybe = canvas.getContext("2d");
  if (!ctxMaybe) throw new Error("2D context unavailable");
  const ctx = ctxMaybe;
  chartStage.appendChild(canvas);

  const loadingEl = document.createElement("div");
loadingEl.style.cssText = `
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  background: var(--chart-bg);
  z-index: 30;
  transition: background 0.3s ease;
`;

loadingEl.innerHTML = `
  <div style="display:flex;gap:6px;align-items:flex-end;height:28px;">
    <span style="width:5px;height:10px;background:var(--chart-primary);animation:cwPulse 0.8s ease-in-out infinite;border-radius:2px;"></span>
    <span style="width:5px;height:18px;background:var(--chart-primary);animation:cwPulse 0.8s ease-in-out 0.1s infinite;border-radius:2px;"></span>
    <span style="width:5px;height:26px;background:var(--chart-primary);animation:cwPulse 0.8s ease-in-out 0.2s infinite;border-radius:2px;"></span>
    <span style="width:5px;height:18px;background:var(--chart-primary);animation:cwPulse 0.8s ease-in-out 0.3s infinite;border-radius:2px;"></span>
    <span style="width:5px;height:10px;background:var(--chart-primary);animation:cwPulse 0.8s ease-in-out 0.4s infinite;border-radius:2px;"></span>
  </div>
  <span class="chart-loading-message" style="font:600 14px 'Manrope',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:var(--chart-text);letter-spacing:0.3px;opacity:0.8;">Loading...</span>
`;

chartStage.appendChild(loadingEl);
const loadingMessageEl = loadingEl.querySelector(".chart-loading-message") as HTMLSpanElement | null;

const animStyle = document.createElement("style");
animStyle.textContent = `
  @keyframes cwPulse {
    0%, 100% {
      opacity: 0.3;
      transform: scaleY(0.6);
    }
    50% {
      opacity: 1;
      transform: scaleY(1);
    }
  }
`;
document.head.appendChild(animStyle);

  const loadingReasons = new Set<string>();
  const visibleLoadingReasons = new Set<string>();
  const loadingRevealTimers = new Map<string, number>();
  let loadingVisible = false;
  let historyBackfillPending = false;
  let historyBackfillBaselineEarliest = 0;
  const shouldShowLoadingReason = (reason: string): boolean => {
    if (reason === "history" && state.preserveAllTrades === true) return false;
    return true;
  };
  const syncLoadingVisibility = () => {
    const shouldShow = visibleLoadingReasons.size > 0;
    const wasVisible = loadingVisible;
    loadingVisible = shouldShow;
    canvas.style.transition = "opacity 160ms ease";
    loadingEl.style.display = shouldShow ? "flex" : "none";
    canvas.style.visibility = "visible";
    canvas.style.opacity = shouldShow ? "0.35" : "1";
    if (loadingMessageEl) {
      loadingMessageEl.textContent = "Loading";
    }
    if (wasVisible && !shouldShow) {
      window.requestAnimationFrame(() => {
        refreshLayoutAndRender();
      });
    }
  };
  const beginLoading = (reason: string, revealDelayMs: number = 0) => {
    if (!shouldShowLoadingReason(reason)) return;
    loadingReasons.add(reason);
    const existingTimer = loadingRevealTimers.get(reason);
    if (existingTimer !== undefined) {
      window.clearTimeout(existingTimer);
      loadingRevealTimers.delete(reason);
    }

    if (revealDelayMs <= 0) {
      visibleLoadingReasons.add(reason);
      syncLoadingVisibility();
      return;
    }

    const timer = window.setTimeout(() => {
      loadingRevealTimers.delete(reason);
      if (!loadingReasons.has(reason)) return;
      visibleLoadingReasons.add(reason);
      syncLoadingVisibility();
    }, revealDelayMs);
    loadingRevealTimers.set(reason, timer);
  };
  const endLoading = (reason: string) => {
    loadingReasons.delete(reason);
    visibleLoadingReasons.delete(reason);
    const timer = loadingRevealTimers.get(reason);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      loadingRevealTimers.delete(reason);
    }
    syncLoadingVisibility();
  };

  const state = options.state ?? model?.state ?? createChartState(container.clientWidth, container.clientHeight);
  bindStateSlices(state, {
    indicatorState: options.indicatorState ?? model?.indicatorState,
    drawingState: options.drawingState ?? model?.drawingState,
    tradeState: options.tradeState ?? model?.tradeState,
  });
  bindChartPersistence(state, persistenceScope);
  normalizeChartTypeForTimeframe(state);
  state.themeName = options.theme ?? state.themeName;
  state.tightPriceScale = options.tightPriceScale ?? state.tightPriceScale ?? false;
  applyThemeToInterface(state.themeName);
  const displaySettings = options.displaySettings ?? model?.displaySettings ?? state.displaySettings;
  if (options.displaySettingsPatch) Object.assign(displaySettings, options.displaySettingsPatch);
  state.displaySettings = displaySettings;
  restoreChartPersistence(state);
  if (options.dataStore) {
    state.chartData = options.dataStore.getData();
  } else if (hasStaticData) {
    state.chartData = Array.isArray(staticData)
      ? normalizeChartDataPoints(staticData, symbol)
      : [];
  }
  let rafId: number | null = null;
  let initialLayoutRetryTimer: number | null = null;
  const INITIAL_LAYOUT_TIMEOUT_MS = 5000;
  let initialRenderScheduled = false;
  let initialLayoutStartedAt = 0;
  let lastKnownLatestTime = 0;
  let lastKnownEarliestTime = 0;
  let awaitingFreshMarketData = !hasStaticData && autoStartLiveData;
  let activeSymbol = marketSelection.symbol ?? liveData.getSelectedSymbol();
  let dataStoreUnsubscribe: (() => void) | null = null;
  let lastLeftEdgeCheck = 0;
  let leftEdgeTriggered = false;
  let historyLoadingManual = false;
  state.suppressMarketFollow = false;
  state.marketHandoffFreezeUntil = 0;

  const marketChangeListeners = new Set<(symbol: string) => void>();

  const checkEdges = () => {
    if (!state.chartData || state.chartData.length === 0) return;
    
    const now = performance.now();
    if (now - lastLeftEdgeCheck < 100) return;
    lastLeftEdgeCheck = now;
    
    const earliestDataTime = getEarliestDataTime(state.chartData);
    const timeStartMs = state.timeStart;
    const bufferMs = 100; // Small buffer to prevent edge flickering
    
    // Check left edge (historical data)
    if (timeStartMs <= earliestDataTime + bufferMs) {
      if (!leftEdgeTriggered) {
        leftEdgeTriggered = true;
        console.log("[Chart] Left edge reached - triggering fetch");
        historyBackfillPending = true;
        historyBackfillBaselineEarliest = earliestDataTime;
        if (historyLoadingManual) beginLoading("history", 0);
        options.onLeftEdgeReached?.();
      }
    } else {
      leftEdgeTriggered = false;
    }
  };

  const notifyMarketChange = (newSymbol: string) => {
    marketChangeListeners.forEach(callback => {
      try {
        callback(newSymbol);
      } catch (error) {
        console.error("Error in market change callback:", error);
      }
    });
    if (options.onMarketChange) {
      try {
        options.onMarketChange(newSymbol);
      } catch (error) {
        console.error("Error in onMarketChange callback:", error);
      }
    }
  };

  const requestHistoricalBackfill = () => {
    if (!shouldBindLiveData || hasStaticData) return;
    if (typeof liveData.requestHistoryBackfill !== "function") return;
    if (state.chartData.length === 0) return;

    const earliestMs = getEarliestDataTime(state.chartData);
    const visibleWindowMs = Math.max(
      state.timeEnd - state.timeStart,
      TIMEFRAME[state.timeframe].step * 50,
    );
    const triggerBufferMs = Math.max(
      visibleWindowMs * 0.25,
      TIMEFRAME[state.timeframe].step * 10,
    );

    if (state.timeStart > earliestMs + triggerBufferMs) return;
    historyBackfillPending = true;
    historyBackfillBaselineEarliest = earliestMs;
    if (historyLoadingManual) beginLoading("history", 0);
    liveData.requestHistoryBackfill(earliestMs, state.timeframe, visibleWindowMs);
  };

  const nudgeViewportAfterBackfill = () => {
    if (state.chartData.length === 0) return;
    const visibleWindowMs = state.timeEnd - state.timeStart;
    if (visibleWindowMs <= 0) return;
    const shiftMs = Math.max(
      TIMEFRAME[state.timeframe].step * 5,
      visibleWindowMs * 0.25,
    );
    state.timeStart -= shiftMs;
    state.timeEnd -= shiftMs;
  };

  function applyInteractiveGutters(width: number) {
    if (!showControls) return;
    state.left = GUTTERS.left;
    state.right = width >= 720 ? 74 : 62;
    state.top = GUTTERS.top;
    state.bottom = GUTTERS.bottom;
  }

  function applySize(allowFallback = true): boolean {
    const dpr = window.devicePixelRatio || 1;
    const rect = chartStage.getBoundingClientRect();
    const measuredWidth = Math.floor(rect.width);
    const measuredHeight = Math.floor(rect.height);
    if (!allowFallback && (measuredWidth <= 1 || measuredHeight <= 1)) {
      return false;
    }
    const width = Math.max(1, measuredWidth > 1 ? measuredWidth : container.clientWidth || window.innerWidth);
    const height = Math.max(1, measuredHeight > 1 ? measuredHeight : container.clientHeight || window.innerHeight);
    state.width = width;
    state.height = height;
    applyInteractiveGutters(width);

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    markStaticDirty();
    return true;
  }

  function requestRender() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      drawChart(ctx, state);
    });
  }

  function refreshLayoutAndRender(): void {
    if (!applySize(false)) {
      if (initialLayoutStartedAt === 0) {
        initialLayoutStartedAt = performance.now();
      }
      if (performance.now() - initialLayoutStartedAt > INITIAL_LAYOUT_TIMEOUT_MS) return;
      if (initialLayoutRetryTimer !== null) {
        window.clearTimeout(initialLayoutRetryTimer);
      }
      initialLayoutRetryTimer = window.setTimeout(() => {
        refreshLayoutAndRender();
      }, 50);
      return;
    }
    initialLayoutStartedAt = 0;
    if (initialLayoutRetryTimer !== null) {
      window.clearTimeout(initialLayoutRetryTimer);
      initialLayoutRetryTimer = null;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    fitPriceRangeInstant(state);
    syncOverlayIndicatorPriceRange(state);
    markStaticDirty();
    requestRender();
  }

  function scheduleInitialRender(): void {
    if (initialRenderScheduled) return;
    initialRenderScheduled = true;
    const run = () => {
      initialLayoutStartedAt = performance.now();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          refreshLayoutAndRender();
        });
      });
    };

    if (document.readyState === "complete") {
      run();
    } else {
      window.addEventListener("load", run, { once: true });
    }
    if ("fonts" in document) {
      (document as Document & { fonts?: FontFaceSet }).fonts?.ready.then(() => {
        refreshLayoutAndRender();
      }).catch(() => {
        // Ignore font loading errors; the resize observer still recovers.
      });
    }
  }

  function resetWindowToTimeframe(tf: Timeframe) {
    const cfg = TIMEFRAME[tf];
    const latestPoint = state.chartData[state.chartData.length - 1];
    const nowMs = latestPoint ? latestPoint.epoch * 1000 : Date.now();
    const earliestMs = getEarliestDataTime(state.chartData);
    const latestMs = getLatestDataTime(state.chartData);
    const dataSpanMs = Math.max(1, latestMs - earliestMs);
    const minUsefulWindowMs = Math.max(TIMEFRAME[tf].step * 8, 30_000);
    const maxPannableWindowMs = Math.max(minUsefulWindowMs, dataSpanMs * 0.9);
    const windowMs = Math.min(cfg.defaultRange, maxPannableWindowMs);
    const rightPadMs = windowMs * 0.3;
    state.timeStart = nowMs + rightPadMs - windowMs;
    state.timeEnd = nowMs + rightPadMs;
  }

  function initializeFreshMarketView(data: ChartMainDataPoint[]): void {
    if (data.length === 0) return;
    state.chartData = data;
    lastKnownLatestTime = getLatestDataTime(data);
    awaitingFreshMarketData = false;
    historyBackfillPending = false;
    
    const latestTime = getLatestDataTime(data);
    const cfg = TIMEFRAME[state.timeframe];
    const windowMs = cfg.defaultRange;
    
    // ===== FIX: Use minimal right padding for fresh market views =====
    // 5% padding instead of 30% so data shows at the right edge
    const rightPadMs = windowMs * 0.3;
    
    state.timeStart = latestTime + rightPadMs - windowMs;
    state.timeEnd = latestTime + rightPadMs;
    
    state.timeZoomLevel = 0;
    state.priceZoomLevel = 0;
    state.suppressMarketFollow = false;
    state.marketHandoffFreezeUntil = 0;
    state.isMarketLive = true;
    state.marketStatus = "open";
    
    fitPriceRangeInstant(state);
    syncOverlayIndicatorPriceRange(state);
    markStaticDirty();
    refreshLayoutAndRender();
    endLoading("initial");
    endLoading("market");
    endLoading("history");
  }

  function applyTimeframeImmediate(tf: Timeframe) {
    state.chartData = getData();
    ticksToOHLC(state.chartData, tf);
    state.timeframe = tf;
    normalizeChartTypeForTimeframe(state);
    state.timeZoomLevel = 0;
    state.priceZoomLevel = 0;
    resetWindowToTimeframe(tf);
    fitPriceRangeInstant(state);
    syncOverlayIndicatorPriceRange(state);
    markStaticDirty();
    requestRender();
  }

  function cleanupOldState(activeTimeframe: Timeframe): void {
    const allTimeframes: Timeframe[] = ["1t", "1m", "2m", "3m", "5m", "10m", "15m", "30m", "1h", "2h", "4h", "8h", "1D"];
    for (const tf of allTimeframes) {
      if (tf !== activeTimeframe) invalidateTimeframeCache(tf);
    }
  }

  function precomputeBackgroundTimeframes(): void {
    const targets: Timeframe[] = ["1m", "5m", "15m", "30m", "1h"];
    const run = () => {
      for (const tf of targets) {
        if (tf !== state.timeframe) precomputeTimeframeCache(state.chartData, tf);
      }
    };

    if ("requestIdleCallback" in window) {
      (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback(run, {
        timeout: 3000,
      });
    } else {
      globalThis.setTimeout(run, 300);
    }
  }

  if (autoStartLiveData) {
    beginLoading("initial");
    liveData.start();
  }

  state.chartData = hasStaticData ? getData() : [];
  if (hasStaticData) {
    lastKnownLatestTime = getLatestDataTime(state.chartData);
    resetWindowToTimeframe(state.timeframe);
    fitPriceRangeInstant(state);
    syncOverlayIndicatorPriceRange(state);
    beginLoading("initial");
    window.setTimeout(() => endLoading("initial"), SIMULATED_LOAD_MS);
  }
  scheduleInitialRender();

  const floatingLatestButton = createFloatingLatestButton(state, requestRender, 0.3, () => {
    state.suppressMarketFollow = false;
    state.marketHandoffFreezeUntil = 0;
  });
  const floatingLatestButtonWithUpdate = floatingLatestButton as HTMLButtonElement & { updateVisibility?: () => void };
  chartStage.appendChild(floatingLatestButton);

  function setTimeframe(tf: Timeframe) {
    // Store current chart type
    const currentChartType = state.chartType;
    const previousTimeframe = state.timeframe;
    
    state.timeframe = tf;
    
    // If switching to 1t and current chart type is candle-based, switch to area
    if (tf === "1t" && CANDLE_BASED_CHART_TYPES.includes(currentChartType)) {
      state.chartType = "area";
    }
    
    normalizeChartTypeForTimeframe(state);
    markStaticDirty();
    requestRender();
    state.suppressMarketFollow = false;
    state.marketHandoffFreezeUntil = 0;
    if (shouldBindLiveData) {
      liveData.updateForTimeframe?.(tf);
    }
    if (tf !== previousTimeframe) {
      options.onTimeframeChange?.(tf);
    }
    beginLoading("timeframe");
    
    window.setTimeout(() => {
      applyTimeframeImmediate(tf);
      cleanupOldState(tf);
      endLoading("timeframe");

      goToLatest(state, 0.3);
      
      // Update the timeframe button in controls if it exists
      if (bottomControls && bottomControls.refreshTimeframeButton) {
        bottomControls.refreshTimeframeButton(tf);
      }
    }, SIMULATED_LOAD_MS);
  }

  function setTheme(theme: ChartThemeName) {
    state.themeName = theme;
    applyThemeToInterface(theme);
    markStaticDirty();
    requestRender();
  }

  function resetPersistedSetupToDefaults(): void {
    state.indicatorType = "none";
    state.indicatorPeriod = 20;
    state.indicatorColor = "#1d4ed8";
    state.indicatorLineWidth = 2;
    state.indicators = [];
    state.activeDrawingTool = null;
    state.currentDrawing = null;
    state.selectedDrawingId = null;
    state.hoveredDrawingId = null;
    state.draggingDrawing = null;
    state.drawings = [];
  }

  function clearTradeMarkersForMarketSwitch(): void {
    if (state.trades.length === 0) return;
    state.trades = [];
    markStaticDirty();
    requestRender();
  }

  function setDisplaySettings(settings: Partial<ChartDisplaySettings>) {
    Object.assign(state.displaySettings, settings);
    markStaticDirty();
    requestRender();
  }

  const clearUserSetup = () => {
    clearChartPersistence(state);
    resetPersistedSetupToDefaults();
    markStaticDirty();
    requestRender();
  };

  function setMarketSymbol(nextSymbol: string) {
    if (!nextSymbol || nextSymbol === marketSelection.symbol) return;
    marketSelection.symbol = nextSymbol;
    clearTradeMarkersForMarketSwitch();
    liveData.selectSymbol(nextSymbol);
  }
  
  let bottomControls: (HTMLDivElement & { 
    refreshTimeframeButton?: (tf: Timeframe) => void; 
    destroy?: () => void 
  }) | null = null;
  let tradePanel: HTMLElement | null = null;
  let sidebar: HTMLElement | null = null;

  if (showControls) {
    bottomControls = createTradingWorkspaceControls(
      state, 
      refreshLayoutAndRender, 
      setTimeframe, 
      liveData, 
      marketSelection,
      {
        showMarketSelector,
        showTimeframeSelector,
        showIndicatorsButton,
        showDrawingsButton,
        showSettingsButton,
        showChartTypeButton,
        iconSize: options.iconSize || 18,
      }
    );
    chartStage.appendChild(bottomControls);

    if (showTradePanel) {
      sidebar = document.createElement("aside");
      sidebar.className = "chart-main-sidebar";
      shell.appendChild(sidebar);
      tradePanel = createTradePanel(state, requestRender, setTimeframe);
      sidebar.appendChild(tradePanel);
    }
  }

  // In createChart.ts, update the syncDataStore function
  if (options.dataStore) {
    const syncDataStore = () => {
      const nextData = options.dataStore!.getData();

      if (nextData.length === 0) {
        state.chartData = [];
        markStaticDirty();
        refreshLayoutAndRender();
        endLoading("history");
        historyBackfillPending = false;

        if (bottomControls && (bottomControls as any).updateMarketButton) {
          (bottomControls as any).updateMarketButton();
        }
        return;
      }

      // ===== CHECK IF THIS IS A MARKET SWITCH =====
      // Must check awaitingFreshMarketData BEFORE assigning chartData so we
      // never render old-market data at the new viewport position (the
      // "forward then snap" visual glitch).
      if (awaitingFreshMarketData) {
        // Wipe old data first so no stale candles are drawn during the transition
        state.chartData = [];
        initializeFreshMarketView(nextData);

        if (nextData.length > 10) {
          awaitingFreshMarketData = false;
        }

        if (bottomControls && (bottomControls as any).updateMarketButton) {
          (bottomControls as any).updateMarketButton();
        }
        return;
      }

      const previousLength = state.chartData.length;
      const wasEmpty = previousLength === 0;
      state.chartData = nextData;

      if (wasEmpty) {
        initializeFreshMarketView(nextData);
        endLoading("history");
        historyBackfillPending = false;
        if (bottomControls && (bottomControls as any).updateMarketButton) {
          (bottomControls as any).updateMarketButton();
        }
        return;
      }

      const nextEarliestTime = getEarliestDataTime(nextData);
      if (
        historyBackfillPending &&
        (nextEarliestTime < historyBackfillBaselineEarliest || nextData.length - previousLength > 1)
      ) {
        endLoading("history");
        historyBackfillPending = false;
        leftEdgeTriggered = false;
      }

      if (!state.suppressMarketFollow && !state.isDragging) {
        // Normal update - go to latest if not suppressed
        goToLatest(state, 0.3);
        
        state.isMarketLive = true;
        state.marketStatus = "open";
        
        if (bottomControls && (bottomControls as any).updateMarketButton) {
          (bottomControls as any).updateMarketButton();
        }
      } else {
        state.isMarketLive = true;
        state.marketStatus = "open";
        
        if (bottomControls && (bottomControls as any).updateMarketButton) {
          (bottomControls as any).updateMarketButton();
        }
      }

      if (state.useDataRange) fitPriceRangeInstant(state);
      syncOverlayIndicatorPriceRange(state);
      markStaticDirty();
      requestRender();
    };

    dataStoreUnsubscribe = options.dataStore.subscribe(syncDataStore);
    syncDataStore();
  }

  applySize();
  fitPriceRangeInstant(state);
  refreshLayoutAndRender();

  setupChartEvents({
    canvas,
    container,
    ctx,
    dpr: window.devicePixelRatio || 1,
    state,
    redraw: requestRender,
    onVisibilityChange: () => {
      // Don't auto-suppress - let the user's pan state persist
      // Only mark static dirty and update button
      markStaticDirty();
      floatingLatestButtonWithUpdate.updateVisibility?.();
      requestHistoricalBackfill();
      checkEdges();
    },
  });

  const marketListener = () => {
    const nextSymbol = liveData.getSelectedSymbol();
    if (nextSymbol === activeSymbol) return;
    activeSymbol = nextSymbol;
    marketSelection.symbol = nextSymbol;
    persistenceScope.symbol = nextSymbol;
    clearTradeMarkersForMarketSwitch();
    awaitingFreshMarketData = true;
    lastKnownLatestTime = 0;
    lastKnownEarliestTime = 0;

    // Clear stale data immediately so no old-market candles are drawn
    // while waiting for new data. Show loading with no delay for the same reason.
    state.chartData = [];
    state.suppressMarketFollow = false;
    state.marketHandoffFreezeUntil = 0;
    beginLoading("market", 0);
    resetPersistedSetupToDefaults();
    restoreChartPersistence(state);
    markStaticDirty();
    requestRender();

    notifyMarketChange(nextSymbol);
  };
  if (shouldBindLiveData) liveData.addMarketListener(marketListener);

  const onResize = () => {
    applySize();
    fitPriceRangeInstant(state);
    syncOverlayIndicatorPriceRange(state);
    markStaticDirty();
    requestRender();
  };
  window.addEventListener("resize", onResize);

  let resizeObserver: ResizeObserver | null = null;
  if ("ResizeObserver" in window) {
    resizeObserver = new ResizeObserver(() => {
      refreshLayoutAndRender();
    });
    resizeObserver.observe(chartStage);
  }

  const precomputeTimeout = window.setTimeout(() => precomputeBackgroundTimeframes(), 1000);
  const tradeCountdownTimer = window.setInterval(() => {
    if (!hasActiveTrades(state)) return;
    requestRender();
  }, 250);

  const destroy = () => {
    window.clearTimeout(precomputeTimeout);
    window.clearInterval(tradeCountdownTimer);
    if (rafId !== null) cancelAnimationFrame(rafId);
    if (initialLayoutRetryTimer !== null) window.clearTimeout(initialLayoutRetryTimer);
    for (const timer of loadingRevealTimers.values()) window.clearTimeout(timer);
    if (shouldBindLiveData) liveData.removeListener(liveListener);
    if (shouldBindLiveData) liveData.removeMarketListener(marketListener);
    dataStoreUnsubscribe?.();
    window.removeEventListener("resize", onResize);
    resizeObserver?.disconnect();
    floatingLatestButton.remove();
    bottomControls?.destroy?.();
    bottomControls?.remove();
    tradePanel?.remove();
    sidebar?.remove();
    loadingEl.remove();
    canvas.remove();
    animStyle.remove();
    clearAllCache();
    marketChangeListeners.clear();
  };

  function getCurrentSymbol(): string {
    return activeSymbol;
  }

  function setHistoryLoading(loading: boolean): void {
    if (loading) {
      historyLoadingManual = true;
      historyBackfillPending = true;
      beginLoading("history", 0);
      return;
    }

    historyLoadingManual = false;
    historyBackfillPending = false;
    leftEdgeTriggered = false;
    endLoading("history");
  }

  // ===== RESET FOR NEW MARKET =====
  function resetForNewMarket(symbol: string): void {
    if (!symbol || symbol === activeSymbol) return;

    activeSymbol = symbol;
    marketSelection.symbol = symbol;
    persistenceScope.symbol = symbol;
    clearTradeMarkersForMarketSwitch();
    awaitingFreshMarketData = true;
    lastKnownLatestTime = 0;
    lastKnownEarliestTime = 0;
    historyBackfillPending = false;

    // Clear stale data and show loading immediately — same principle as
    // marketListener: no old candles should be visible while waiting for new data.
    state.chartData = [];
    state.suppressMarketFollow = false;
    state.marketHandoffFreezeUntil = 0;
    state.isMarketLive = false;
    state.marketStatus = "unknown";
    state.timeZoomLevel = 0;
    state.priceZoomLevel = 0;
    resetPersistedSetupToDefaults();
    restoreChartPersistence(state);

    beginLoading("market", 0);
    notifyMarketChange(symbol);
    markStaticDirty();
    requestRender();

    if (bottomControls && (bottomControls as any).updateMarketButton) {
      setTimeout(() => {
        (bottomControls as any).updateMarketButton();
      }, 100);
    }
  }

  const liveListener = (newData: typeof state.chartData) => {
    if (newData.length === 0) {
      awaitingFreshMarketData = true;
      lastKnownLatestTime = 0;
      lastKnownEarliestTime = 0;
      beginLoading("market", 240);
      state.chartData = newData;
      markStaticDirty();
      requestRender(); 
      checkEdges();
      return;
    }

    const newEarliestTime = getEarliestDataTime(newData);
    const newLatestTime = getLatestDataTime(newData);
    const backfillExtendedHistory = lastKnownEarliestTime !== 0 && newEarliestTime < lastKnownEarliestTime;
    if (backfillExtendedHistory) {
      endLoading("history");
      historyBackfillPending = false;
      leftEdgeTriggered = false;
    }

    // ===== HANDLE MARKET SWITCH - set viewport BEFORE assigning chartData =====
    // Assigning chartData before repositioning the viewport causes one rendered
    // frame where new-market candles appear at the old-market viewport position,
    // producing the visible "flicker forward then snap back" effect.
    if (awaitingFreshMarketData || lastKnownLatestTime === 0) {
      const latestTime = getLatestDataTime(newData);
      const cfg = TIMEFRAME[state.timeframe];
      const windowMs = cfg.defaultRange;
      const rightPadMs = windowMs * 0.3;
      
      state.timeStart = latestTime + rightPadMs - windowMs;
      state.timeEnd = latestTime + rightPadMs;
      state.timeZoomLevel = 0;
      state.priceZoomLevel = 0;
      
      // Assign data only after the viewport is correctly positioned
      state.chartData = newData;
      lastKnownLatestTime = newLatestTime;
      lastKnownEarliestTime = newEarliestTime;
      awaitingFreshMarketData = false;
      state.suppressMarketFollow = false;
      state.marketHandoffFreezeUntil = 0;
      state.isMarketLive = true;
      state.marketStatus = "open";
      
      fitPriceRangeInstant(state);
      syncOverlayIndicatorPriceRange(state);
      markStaticDirty();
      requestRender();
      endLoading("initial");
      endLoading("market");
      endLoading("history");
      historyBackfillPending = false;
      return;
    }

    // Normal update — safe to assign data now since viewport is already correct
    state.chartData = newData;
    state.isMarketLive = true;
    state.marketStatus = "open";

    endLoading("initial");
    endLoading("market");
    if (backfillExtendedHistory) {
      endLoading("history");
      historyBackfillPending = false;
      leftEdgeTriggered = false;
    }

    // Normal update logic...
    if (!state.suppressMarketFollow && newLatestTime > lastKnownLatestTime) {
      const windowMs = state.timeEnd - state.timeStart;
      const rightPadMs = windowMs * 0.3;
      const expectedEnd = lastKnownLatestTime + rightPadMs;
      const isAtLatest = Math.abs(state.timeEnd - expectedEnd) < windowMs * 0.3;
      
      if (isAtLatest) {
        state.timeEnd = newLatestTime + rightPadMs;
        state.timeStart = state.timeEnd - windowMs;
      }
      
      lastKnownLatestTime = newLatestTime;
    } else if (newLatestTime > lastKnownLatestTime) {
      lastKnownLatestTime = newLatestTime;
    }

    if (state.suppressMarketFollow) {
      state.suppressMarketFollow = false;
    }

    // Handle pending timeframe switch
    if ((state as any).__pendingTimeframeSwitch) {
      const pending = (state as any).__pendingTimeframeSwitch;
      if (pending.to === "1t" && state.timeframe !== "1t") {
        setTimeframe("1t");
      }
      delete (state as any).__pendingTimeframeSwitch;
    }

    if (backfillExtendedHistory) {
      nudgeViewportAfterBackfill();
      if (state.useDataRange) fitPriceRangeInstant(state);
      syncOverlayIndicatorPriceRange(state);
      markStaticDirty();
      requestRender();
      lastKnownEarliestTime = newEarliestTime;
      return;
    }

    if (state.useDataRange) fitPriceRangeInstant(state);
    syncOverlayIndicatorPriceRange(state);
    markStaticDirty();
    requestRender();
    lastKnownEarliestTime = newEarliestTime;
  };

  if (shouldBindLiveData) liveData.addListener(liveListener);

  return {
    container,
    canvas,
    state,
    requestRender,
    setTimeframe,
    setTheme,
    setDisplaySettings,
    setMarketSymbol,
    resize: onResize,
    destroy,
    clearUserSetup,
    onMarketChange: (callback: (symbol: string) => void) => {
      marketChangeListeners.add(callback);
      return () => marketChangeListeners.delete(callback);
    },
    resetForNewMarket,
    getCurrentSymbol,
    setHistoryLoading,
  };
}

export function createChart(options: ChartOptions): ChartMainInstance {
  return createChartBase({
    ...options,
    showControls: false,
  });
}

export function createChartMain(options: ChartMainOptions): ChartMainInstance {
  return createChartBase({
    ...options,
    showControls: options.showControls ?? true,
    isMarketLive: options.isMarketLive,
    marketStatus: options.marketStatus,
  });
}
